import React, { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Image,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import TonyteBackground from '../components/TonyteBackground';
import { auth } from '../firebase';
import { useProfile } from '../hooks/useProfile';
import { listAdminPromotions } from '../services/promotionAdmin';
import { Typography } from '../theme';
import { useTheme } from '../theme/useTheme';
import type { TonyteTheme } from '../theme/themes';
import { useI18n } from '../i18n';
import type { RootStackParamList } from '../types';
import { SUPPORTED_CITIES } from '../utils/cities';
import {
  STATUS_ORDER,
  computePromotionStatus,
  formatBerlin,
  hasUnpublishedChanges,
  type AdminPromotion,
  type PromotionStatus,
} from '../utils/promotionAdmin';

const FILTERS: Array<{ key: 'all' | PromotionStatus; labelKey: string }> = [
  { key: 'all', labelKey: 'promotionsAdmin.filterAll' },
  { key: 'active', labelKey: 'promotionsAdmin.statusActive' },
  { key: 'scheduled', labelKey: 'promotionsAdmin.statusScheduled' },
  { key: 'draft', labelKey: 'promotionsAdmin.statusDraft' },
  { key: 'ended', labelKey: 'promotionsAdmin.statusEnded' },
  { key: 'archived', labelKey: 'promotionsAdmin.statusArchived' },
];

export const STATUS_LABEL_KEYS: Record<PromotionStatus, string> = {
  draft: 'promotionsAdmin.statusDraft',
  scheduled: 'promotionsAdmin.statusScheduled',
  active: 'promotionsAdmin.statusActive',
  ended: 'promotionsAdmin.statusEnded',
  disabled: 'promotionsAdmin.statusDisabled',
  archived: 'promotionsAdmin.statusArchived',
};

export const STATUS_COLORS: Record<PromotionStatus, { fg: string; bg: string }> = {
  active: { fg: '#16A34A', bg: 'rgba(34,197,94,0.14)' },
  scheduled: { fg: '#0284C7', bg: 'rgba(56,189,248,0.16)' },
  draft: { fg: '#8A8F98', bg: 'rgba(148,163,184,0.16)' },
  disabled: { fg: '#8A8F98', bg: 'rgba(148,163,184,0.16)' },
  ended: { fg: '#64748B', bg: 'rgba(100,116,139,0.14)' },
  archived: { fg: '#64748B', bg: 'rgba(100,116,139,0.10)' },
};

const isAdminRole = (role?: string) => role === 'admin' || role === 'co-admin';

// Admin/co-admin promotion management on mobile (and the webapp). Same data
// and rules as the dashboard's Promotions section.
const AdminPromotionsScreen: React.FC = () => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const themed = useMemo(() => createThemedStyles(theme), [theme]);
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { profile, loading: profileLoading } = useProfile(auth.currentUser?.uid || null);
  const [rows, setRows] = useState<AdminPromotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | PromotionStatus>('all');
  const allowed = isAdminRole(profile?.role);

  const load = useCallback(async () => {
    try {
      setError(null);
      setRows(await listAdminPromotions());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(useCallback(() => {
    if (allowed) void load();
  }, [allowed, load]));

  const now = Date.now();
  const withStatus = rows
    .map(row => ({ row, status: computePromotionStatus(row.draft, row.published, now) }))
    .sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status)
      || a.row.draft.priority - b.row.draft.priority
      || (a.row.draft.startAt || 0) - (b.row.draft.startAt || 0));
  const visible = withStatus.filter(({ status }) => filter === 'all' || status === filter || (filter === 'draft' && status === 'disabled'));

  const header = (
    <View style={[styles.header, { paddingTop: insets.top + 10 }]}>
      <TouchableOpacity style={[styles.iconButton, themed.surface]} onPress={() => navigation.goBack()} accessibilityRole="button" accessibilityLabel={t('common.back')}>
        <Icon name="arrow-left" size={20} color={theme.colors.icon} />
      </TouchableOpacity>
      <View style={styles.headerCopy}>
        <Text style={[styles.kicker, themed.textMuted]}>{t('promotionsAdmin.kicker')}</Text>
        <Text style={[styles.title, themed.textPrimary]}>{t('promotionsAdmin.title')}</Text>
      </View>
      {allowed ? (
        <TouchableOpacity style={styles.newButton} onPress={() => navigation.navigate('AdminPromotionEditor', {})} accessibilityRole="button">
          <Icon name="plus" size={16} color="#fff" />
          <Text style={styles.newButtonText}>{t('promotionsAdmin.new')}</Text>
        </TouchableOpacity>
      ) : null}
    </View>
  );

  if (!profileLoading && !allowed) {
    return (
      <TonyteBackground appearanceAware style={[styles.container, themed.container]}>
        {header}
        <View style={styles.center}>
          <Icon name="lock" size={28} color={theme.colors.iconMuted} />
          <Text style={[styles.emptyTitle, themed.textPrimary]}>{t('promotionsAdmin.adminsOnly')}</Text>
        </View>
      </TonyteBackground>
    );
  }

  return (
    <TonyteBackground appearanceAware style={[styles.container, themed.container]}>
      {header}
      <View>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.filters}>
          {FILTERS.map(f => {
            const active = filter === f.key;
            const count = f.key === 'all' ? rows.length : withStatus.filter(x => x.status === f.key || (f.key === 'draft' && x.status === 'disabled')).length;
            return (
              <TouchableOpacity key={f.key} style={[styles.chip, active ? themed.chipActive : themed.surface]} onPress={() => setFilter(f.key)}>
                <Text style={[styles.chipText, active ? themed.chipActiveText : themed.textSecondary]}>{t(f.labelKey)} · {count}</Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>
      {loading || profileLoading ? (
        <View style={styles.center}><ActivityIndicator color={theme.colors.accentSecondary} /></View>
      ) : (
        <FlatList
          data={visible}
          keyExtractor={item => item.row.id}
          contentContainerStyle={[styles.list, { paddingBottom: insets.bottom + 32 }]}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); void load(); }} />}
          ListHeaderComponent={error ? <Text style={styles.error}>{error}</Text> : null}
          ListEmptyComponent={
            <View style={[styles.empty, themed.surface]}>
              <Text style={[styles.emptyTitle, themed.textPrimary]}>{rows.length ? t('promotionsAdmin.emptyFilter') : t('promotionsAdmin.emptyTitle')}</Text>
              {!rows.length ? <Text style={[styles.emptyText, themed.textSecondary]}>{t('promotionsAdmin.emptySub')}</Text> : null}
            </View>
          }
          renderItem={({ item: { row, status } }) => {
            const d = row.draft;
            const colors = STATUS_COLORS[status];
            const cities = d.cityKeys.length
              ? d.cityKeys.map(c => SUPPORTED_CITIES.find(x => x.value === c)?.label || c).join(', ')
              : t('promotionsAdmin.allCities');
            return (
              <TouchableOpacity style={[styles.row, themed.surface]} onPress={() => navigation.navigate('AdminPromotionEditor', { promotionId: row.id })} accessibilityRole="button">
                <View style={styles.thumb}>
                  <LinearGradient colors={['#1E2A44', '#3A2A4A', '#F27D26']} style={StyleSheet.absoluteFill} />
                  {d.coverImageUrl ? <Image source={{ uri: d.coverImageUrl }} style={StyleSheet.absoluteFill} resizeMode="cover" /> : null}
                </View>
                <View style={styles.rowCopy}>
                  <Text style={[styles.rowTitle, themed.textPrimary]} numberOfLines={1}>{d.title || t('promotionsAdmin.untitled')}</Text>
                  <View style={styles.pills}>
                    <View style={[styles.pill, { backgroundColor: colors.bg }]}><Text style={[styles.pillText, { color: colors.fg }]}>{t(STATUS_LABEL_KEYS[status])}</Text></View>
                    {hasUnpublishedChanges(d, row.published) ? (
                      <View style={[styles.pill, styles.changesPill]}><Text style={[styles.pillText, styles.changesText]}>{t('promotionsAdmin.unpublishedChanges')}</Text></View>
                    ) : null}
                  </View>
                  <Text style={[styles.rowMeta, themed.textSecondary]} numberOfLines={1}>
                    {formatBerlin((row.published?.published ? row.published : d).startAt)} → {formatBerlin((row.published?.published ? row.published : d).endAt)}
                  </Text>
                  <Text style={[styles.rowMeta, themed.textMuted]} numberOfLines={1}>
                    {cities} · {t('promotions.eventsCount').replace('{count}', String(d.eventIds.length))} · P{d.priority}
                  </Text>
                </View>
                <Icon name="chevron-right" size={18} color={theme.colors.iconMuted} />
              </TouchableOpacity>
            );
          }}
        />
      )}
    </TonyteBackground>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingBottom: 10 },
  iconButton: { width: 40, height: 40, borderRadius: 14, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  headerCopy: { flex: 1, minWidth: 0 },
  kicker: { fontSize: 10, fontWeight: Typography.extrabold, letterSpacing: 1.6, textTransform: 'uppercase' },
  title: { fontSize: 22, fontWeight: Typography.extrabold },
  newButton: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#F27D26', borderRadius: 999, paddingHorizontal: 14, paddingVertical: 9 },
  newButtonText: { color: '#fff', fontSize: 13, fontWeight: Typography.extrabold },
  filters: { paddingHorizontal: 16, gap: 8, paddingBottom: 10 },
  chip: { borderRadius: 999, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 7 },
  chipText: { fontSize: 12, fontWeight: Typography.bold },
  list: { paddingHorizontal: 16, gap: 10 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, borderRadius: 18, borderWidth: 1, padding: 10 },
  thumb: { width: 72, height: 72, borderRadius: 14, overflow: 'hidden' },
  rowCopy: { flex: 1, minWidth: 0, gap: 4 },
  rowTitle: { fontSize: 15, fontWeight: Typography.extrabold },
  pills: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  pill: { borderRadius: 999, paddingHorizontal: 8, paddingVertical: 2 },
  pillText: { fontSize: 11, fontWeight: Typography.extrabold },
  changesPill: { backgroundColor: 'rgba(242,125,38,0.14)' },
  changesText: { color: '#E56816' },
  rowMeta: { fontSize: 12, fontWeight: Typography.medium },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 10 },
  empty: { borderRadius: 18, borderWidth: 1, padding: 20, alignItems: 'center', gap: 6 },
  emptyTitle: { fontSize: 16, fontWeight: Typography.extrabold, textAlign: 'center' },
  emptyText: { fontSize: 13, textAlign: 'center', lineHeight: 19 },
  error: { color: '#EF4444', marginBottom: 10 },
});

const createThemedStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: { backgroundColor: theme.colors.background },
  textPrimary: { color: theme.colors.textPrimary },
  textSecondary: { color: theme.colors.textSecondary },
  textMuted: { color: theme.colors.textMuted },
  surface: { backgroundColor: theme.colors.surfaceElevated, borderColor: theme.colors.border },
  chipActive: { backgroundColor: theme.colors.accentSecondary, borderColor: theme.colors.accentSecondary },
  chipActiveText: { color: theme.isDark ? '#04201F' : '#FFFFFF' },
});

export default AdminPromotionsScreen;
